import { Link, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Minimize2 } from 'lucide-react';
import { Button } from './Button';

export interface PanelReturnTarget {
  path: string;
  label: string;
}

/** Accept only local source URLs; a direct visit has no return context to invent. */
export function panelReturnTarget(state: unknown): PanelReturnTarget | null {
  if (!state || typeof state !== 'object') return null;
  const back = (state as { backTo?: unknown }).backTo;
  if (!back || typeof back !== 'object') return null;
  const { path, label } = back as { path?: unknown; label?: unknown };
  if (typeof path !== 'string' || !path.startsWith('/') || path.startsWith('//') || typeof label !== 'string' || !label.trim()) return null;
  // Browsers normalize backslashes and strip control characters in navigable URLs.
  if ([...path].some((c) => c.charCodeAt(0) < 32 || c.charCodeAt(0) === 92)) return null;
  return { path, label };
}

/** The same origin is consumed by full pages and full-page destinations rendered as sheets. */
export function usePanelReturn(): PanelReturnTarget | null {
  return panelReturnTarget(useLocation().state);
}

/** Restore the original source URL, including its open panel, without a full-page/back loop. */
export function RestorePanelButton({ origin }: { origin: PanelReturnTarget | null }) {
  const { t } = useTranslation();
  if (!origin) return null;
  const restoresPanel = new URL(origin.path, 'https://panel.invalid').searchParams.has('panel');
  return (
    <Button asChild size="sm">
      <Link to={origin.path} replace title={t('backTo', { label: origin.label })}>
        {restoresPanel ? <Minimize2 aria-hidden /> : <ArrowLeft aria-hidden />}
        {restoresPanel ? t('panel.restore') : t('backTo', { label: origin.label })}
      </Link>
    </Button>
  );
}
